// Minimal, dependency-free ZIP writer (STORE method — no compression) plus helpers to turn a
// picked/dropped folder into a single .zip File. We zip client-side and send it through the
// existing archive-upload path: the backend already content-sniffs the `PK` magic bytes and
// extracts zips, so no server change is needed. STORE is fine here — the upload cap is small and
// Python's zipfile reads stored entries directly; it only needs a correct CRC32 per entry.

// --- CRC32 (standard IEEE polynomial, table-driven) ---
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(buf: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < buf.length; i++) c = CRC_TABLE[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export type ZipEntry = { path: string; data: Uint8Array };

/** Build a valid (STORE, uncompressed) ZIP archive from the given entries. */
export function zipStore(entries: ZipEntry[]): Uint8Array {
  const enc = new TextEncoder();
  const local: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const e of entries) {
    const name = enc.encode(e.path);
    const crc = crc32(e.data);
    const size = e.data.length;

    const lh = new Uint8Array(30 + name.length);
    const lv = new DataView(lh.buffer);
    lv.setUint32(0, 0x04034b50, true); // local file header signature
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0x0800, true); // flags: UTF-8 filenames
    lv.setUint16(8, 0, true); // method: store
    lv.setUint16(10, 0, true); // mod time
    lv.setUint16(12, 0, true); // mod date
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); // compressed size
    lv.setUint32(22, size, true); // uncompressed size
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true); // extra length
    lh.set(name, 30);
    local.push(lh, e.data);

    const ch = new Uint8Array(46 + name.length);
    const cv = new DataView(ch.buffer);
    cv.setUint32(0, 0x02014b50, true); // central dir header signature
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0x0800, true); // flags: UTF-8
    cv.setUint16(10, 0, true); // method
    cv.setUint16(12, 0, true); // time
    cv.setUint16(14, 0, true); // date
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint16(30, 0, true); // extra
    cv.setUint16(32, 0, true); // comment
    cv.setUint16(34, 0, true); // disk number start
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true); // local header offset
    ch.set(name, 46);
    central.push(ch);

    offset += lh.length + size;
  }

  const centralSize = central.reduce((s, c) => s + c.length, 0);
  const centralOffset = offset;

  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true); // end of central directory signature
  ev.setUint16(4, 0, true); // disk number
  ev.setUint16(6, 0, true); // central dir start disk
  ev.setUint16(8, entries.length, true); // entries on this disk
  ev.setUint16(10, entries.length, true); // total entries
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralOffset, true);
  ev.setUint16(20, 0, true); // comment length

  const total = offset + centralSize + eocd.length;
  const out = new Uint8Array(total);
  let p = 0;
  for (const c of local) { out.set(c, p); p += c.length; }
  for (const c of central) { out.set(c, p); p += c.length; }
  out.set(eocd, p);
  return out;
}

// macOS / editor cruft we never want inside an uploaded skill (mirrors the backend's is_junk_path).
export function isJunkPath(path: string): boolean {
  return path
    .split("/")
    .some((seg) => seg === ".DS_Store" || seg === "__MACOSX" || seg === "Thumbs.db" || seg.startsWith("._"));
}

/** Relative path a picked File carries (directory input sets webkitRelativePath). */
export function relPath(f: File): string {
  return (f as unknown as { webkitRelativePath?: string }).webkitRelativePath || f.name;
}

/** True if any file in the set is a SKILL.md (the backend requires one). */
export function hasSkillMd(files: File[]): boolean {
  return files.some((f) => relPath(f).split("/").pop()?.toLowerCase() === "skill.md");
}

/** Zip a set of folder files (preserving their relative paths) into a single .zip File. */
export async function zipFolder(files: File[], zipName: string): Promise<File> {
  const entries: ZipEntry[] = [];
  for (const f of files) {
    const path = relPath(f);
    if (isJunkPath(path)) continue;
    entries.push({ path, data: new Uint8Array(await f.arrayBuffer()) });
  }
  const bytes = zipStore(entries);
  return new File([bytes], zipName, { type: "application/zip" });
}

// --- Drag-and-drop directory traversal (webkitGetAsEntry API) ---
type FsEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (cb: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (cb: (e: FsEntry[]) => void, err: (e: unknown) => void) => void };
};

async function readEntry(entry: FsEntry, prefix: string, out: File[]): Promise<void> {
  if (entry.isFile && entry.file) {
    const file: File = await new Promise((res, rej) => entry.file!(res, rej));
    // Preserve the folder-relative path so the archive keeps its structure.
    Object.defineProperty(file, "webkitRelativePath", { value: prefix + file.name, configurable: true });
    out.push(file);
  } else if (entry.isDirectory && entry.createReader) {
    const reader = entry.createReader();
    // readEntries returns in batches — loop until it yields an empty batch.
    const all: FsEntry[] = [];
    for (;;) {
      const batch: FsEntry[] = await new Promise((res, rej) => reader.readEntries(res, rej));
      if (!batch.length) break;
      all.push(...batch);
    }
    for (const child of all) await readEntry(child, prefix + entry.name + "/", out);
  }
}

/** Collect all files under the dropped FileSystemEntry[] (already gathered synchronously). */
export async function filesFromEntries(entries: FsEntry[]): Promise<File[]> {
  const out: File[] = [];
  for (const e of entries) await readEntry(e, "", out);
  return out;
}
